import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Phone,
  Video,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { conversationsByFamilyAtom, fullScreenPageAtom } from '../store';
import { useAuth, useCall, useFamily, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError, type CallHistoryEntry } from '../lib/api';
import { avatarPairIndex } from '../theme';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { EmptyState } from '../components/EmptyState';
import { appLocale } from '../lib/format';
import { t as tNow, useT } from '../i18n';

// Calls you missed: placed by someone else and never picked up.
const missedByMe = (call: CallHistoryEntry) =>
  !call.outgoing && (call.outcome === 'missed' || call.outcome === 'cancelled');

// Back-to-back missed calls from the same chat read as one row, "Arjun (2)".
interface CallRow {
  call: CallHistoryEntry;
  count: number;
}
function groupRows(calls: CallHistoryEntry[]): CallRow[] {
  const rows: CallRow[] = [];
  for (const call of calls) {
    const last = rows[rows.length - 1];
    if (
      last &&
      missedByMe(call) &&
      missedByMe(last.call) &&
      last.call.conversationId === call.conversationId &&
      last.call.callType === call.callType
    ) {
      last.count += 1;
    } else {
      rows.push({ call, count: 1 });
    }
  }
  return rows;
}

function dayLabel(
  iso: string,
  now = new Date()
): 'Today' | 'Yesterday' | 'Earlier' {
  const date = new Date(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return 'Earlier';
}

const SECTION_LABELS = {
  Today: 'chat.list.today',
  Yesterday: 'chat.list.yesterday',
  Earlier: 'chat.calls.earlier',
} as const;

function timeLabel(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (dayLabel(iso, now) !== 'Earlier') {
    return date.toLocaleTimeString(appLocale(), {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (now.getTime() - date.getTime() < 7 * 86_400_000) {
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  }
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

function minutes(seconds: number): string {
  return seconds < 60
    ? tNow('chat.call.seconds', { n: Math.max(1, seconds) })
    : tNow('chat.call.minutes', { n: Math.round(seconds / 60) });
}

function detail(call: CallHistoryEntry, count: number) {
  const video = call.callType === 'video';
  const kind = tNow(video ? 'chat.calls.video' : 'chat.calls.voice');
  if (missedByMe(call)) {
    return {
      icon: (
        <ArrowDownLeft
          size={15}
          strokeWidth={2.25}
          role="img"
          aria-label={tNow('chat.call.missed')}
        />
      ),
      text: tNow(
        video
          ? count > 1
            ? 'chat.calls.missedVideoOther'
            : 'chat.call.missedVideo'
          : count > 1
            ? 'chat.calls.missedVoiceOther'
            : 'chat.call.missedVoice'
      ),
      missed: true,
    };
  }
  if (call.outcome === 'declined') {
    return {
      icon: (
        <X
          size={15}
          strokeWidth={2.25}
          role="img"
          aria-label={tNow('chat.calls.declinedLabel')}
        />
      ),
      text: tNow(
        call.outgoing ? 'chat.calls.declined' : 'chat.calls.youDeclined',
        { kind }
      ),
      missed: false,
    };
  }
  const direction = call.outgoing ? (
    <ArrowUpRight
      size={15}
      strokeWidth={2.25}
      role="img"
      aria-label={tNow('chat.calls.outgoing')}
    />
  ) : (
    <ArrowDownLeft
      size={15}
      strokeWidth={2.25}
      role="img"
      aria-label={tNow('chat.calls.incoming')}
    />
  );
  if (call.outcome === 'completed') {
    return {
      icon: direction,
      text: `${kind} · ${minutes(call.durationSeconds)}`,
      missed: false,
    };
  }
  return {
    icon: direction,
    text:
      call.outcome === 'cancelled'
        ? tNow('chat.calls.cancelled', { kind })
        : tNow('chat.calls.noAnswer', { kind }),
    missed: false,
  };
}

// Who the call was with: the family tile for "Everyone", a little colour
// grid for other groups, the person for a direct call.
function CallAvatar({
  call,
  familyName,
  familyId,
}: {
  call: CallHistoryEntry;
  familyName: string;
  familyId: string | null;
}) {
  const chats = useAtomValue(conversationsByFamilyAtom);
  if (call.conversation.isDefault) {
    return (
      <Flex
        w="48px"
        h="48px"
        borderRadius="16px"
        bg="accent.solid"
        color="accent.onSolid"
        align="center"
        justify="center"
        fontFamily="heading"
        fontWeight={800}
        fontSize="20px"
        flexShrink={0}
        aria-hidden="true"
      >
        {familyName.charAt(0).toUpperCase()}
      </Flex>
    );
  }
  if (call.conversation.type === 'group') {
    const start = avatarPairIndex(call.conversation.name);
    return (
      <Grid
        w="48px"
        h="48px"
        borderRadius="16px"
        overflow="hidden"
        templateColumns="repeat(2, minmax(0, 1fr))"
        flexShrink={0}
        aria-hidden="true"
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} bg={`avatar.${(start + i) % 6}.bg`} />
        ))}
      </Grid>
    );
  }
  // A direct call's face is the other person's (D292): the caller when they
  // rang, otherwise whoever else is in that chat, from the home's list.
  const otherId = call.outgoing
    ? (familyId ? chats[familyId] : undefined)
        ?.find((chat) => chat.id === call.conversationId)
        ?.participants.find((p) => p.id !== call.caller.id)?.id
    : call.caller.id;
  return (
    <Avatar
      size="md"
      name={call.conversation.name}
      src={call.conversation.avatarUrl ?? undefined}
      ownerId={otherId}
      familyId={familyId}
      flexShrink={0}
    />
  );
}

function NewCallDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { currentFamilyId } = useFamily();
  const { currentUser } = useAuth();
  const { startCall, state } = useCall();
  const t = useT();
  const [chats, setChats] = useState<
    Array<{
      id: string;
      name: string;
      isDefault: boolean;
      type: string;
      avatarUrl: string | null;
      /** A direct chat's other person, whose face it is (D292). */
      avatarOwner: string | null;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  // The small mark in the sheet while the chats load (M66, D250).
  const sheetState = useLoadingState({ loading, hasData: chats.length > 0 });

  useEffect(() => {
    if (!isOpen || !currentFamilyId) return;
    setLoading(true);
    api
      .getConversations(currentFamilyId)
      .then(({ data }) =>
        setChats(
          data
            .map((chat) => {
              const other = chat.participants.find(
                (p) => p.id !== currentUser?.id
              );
              return {
                id: chat.id,
                type: chat.type,
                isDefault: chat.isDefault,
                name: chat.isDefault
                  ? tNow('chat.header.everyone')
                  : (chat.name ??
                    other?.displayName ??
                    tNow('chat.calls.chat')),
                avatarUrl:
                  chat.type === 'direct' ? (other?.avatarUrl ?? null) : null,
                avatarOwner:
                  chat.type === 'direct' ? (other?.id ?? null) : null,
              };
            })
            .sort(
              (a, b) =>
                Number(b.isDefault) - Number(a.isDefault) ||
                a.name.localeCompare(b.name)
            )
        )
      )
      .catch(() => setChats([]))
      .finally(() => setLoading(false));
  }, [isOpen, currentFamilyId, currentUser?.id]);

  const call = (conversationId: string, type: 'voice' | 'video') => {
    onClose();
    void startCall(conversationId, type);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={4} borderRadius="28px" maxH="80vh">
        <ModalHeader>
          <Heading as="h2" fontSize="24px">
            {t('chat.calls.newCall')}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {sheetState.show ? (
            <LoadingPanel state={sheetState} size="inline" />
          ) : (
            <VStack align="stretch" spacing={1}>
              {chats.map((chat) => (
                <HStack key={chat.id} spacing={3} py={2}>
                  <Avatar
                    size="sm"
                    name={chat.name}
                    src={chat.avatarUrl ?? undefined}
                    ownerId={chat.avatarOwner}
                    familyId={currentFamilyId}
                  />
                  <Text flex="1" fontWeight={700} noOfLines={1}>
                    {chat.name}
                  </Text>
                  <IconButton
                    aria-label={t('chat.calls.voiceCallName', {
                      name: chat.name,
                    })}
                    icon={<Phone size={18} />}
                    colorScheme="gray"
                    bg="accent.tint"
                    color="accent"
                    isDisabled={state.status !== 'idle'}
                    onClick={() => call(chat.id, 'voice')}
                  />
                  <IconButton
                    aria-label={t('chat.calls.videoCallName', {
                      name: chat.name,
                    })}
                    icon={<Video size={18} />}
                    colorScheme="gray"
                    bg="accent.tint"
                    color="accent"
                    isDisabled={state.status !== 'idle'}
                    onClick={() => call(chat.id, 'video')}
                  />
                </HStack>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// Calls (Living Room): every call in this family's chats, missed ones in the
// accent with a solid call-back button. Full screen, like Starred.
export function CallsPage() {
  const { currentFamilyId, families } = useFamily();
  const { startCall, state } = useCall();
  const navigate = useNavigate();
  const newCall = useDisclosure();
  const t = useT();
  const [calls, setCalls] = useState<CallHistoryEntry[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'missed'>('all');
  const familyName =
    families.find((f) => f.id === currentFamilyId)?.name ??
    t('chat.home.family');

  const setFullScreen = useSetAtom(fullScreenPageAtom);
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  const load = useCallback(async (familyId: string, after?: string) => {
    if (after) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await api.getCallHistory(familyId, after);
      setCalls((current) =>
        after ? [...current, ...response.data] : response.data
      );
      setHasMore(response.pagination.hasMore);
      setCursor(response.pagination.cursor);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : tNow('common.somethingWrong')
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // The mark while the history loads (M66, D248).
  const callsState = useLoadingState({
    loading,
    hasData: calls.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    // A switch shows the new family's rows, never the previous family's.
    setCalls([]);
    if (currentFamilyId) void load(currentFamilyId);
  }, [currentFamilyId, load]);

  if (!currentFamilyId) return <NoFamilySelected />;

  const missedCount = calls.filter(missedByMe).length;
  const rows = groupRows(
    filter === 'missed' ? calls.filter(missedByMe) : calls
  );
  const sections = (['Today', 'Yesterday', 'Earlier'] as const)
    .map((label) => ({
      label,
      rows: rows.filter((row) => dayLabel(row.call.createdAt) === label),
    }))
    .filter((section) => section.rows.length > 0);

  const chip = (value: 'all' | 'missed', label: string) => {
    const active = filter === value;
    return (
      <Button
        h="40px"
        px="18px"
        fontSize="15px"
        fontWeight={600}
        aria-pressed={active}
        colorScheme="gray"
        bg={active ? 'ink' : 'surface'}
        color={active ? 'canvas' : 'ink'}
        borderWidth={active ? 0 : '1px'}
        borderColor="line"
        _hover={{ opacity: 0.9 }}
        onClick={() => setFilter(value)}
      >
        {label}
      </Button>
    );
  };

  return (
    <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
      <HStack
        spacing={1}
        px={2}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
        minH="56px"
        flexShrink={0}
      >
        <IconButton
          aria-label={t('common.back')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => navigate(-1)}
        />
        <Box flex="1" />
        <Button
          h="44px"
          px={4}
          leftIcon={<Phone size={18} />}
          onClick={newCall.onOpen}
        >
          {t('chat.calls.newCall')}
        </Button>
      </HStack>

      <VStack align="stretch" spacing={3} px={4} pb={3} flexShrink={0}>
        <Heading as="h1" fontSize="32px" lineHeight={1}>
          {t('chat.calls.title')}
        </Heading>
        <HStack spacing={2}>
          {chip('all', t('chat.calls.all'))}
          {chip(
            'missed',
            missedCount > 0
              ? t('chat.calls.missedCount', { n: missedCount })
              : t('chat.call.missed')
          )}
        </HStack>
      </VStack>

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        px={4}
        pb={6}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        {error && !callsState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {callsState.show ? (
          <LoadingPanel state={callsState} label={t('chat.calls.loading')} />
        ) : rows.length === 0 ? (
          <EmptyState
            kind="calls"
            title={t(
              filter === 'missed' ? 'chat.calls.noMissed' : 'chat.calls.none'
            )}
            body={
              filter === 'missed'
                ? t('chat.calls.caughtUp')
                : t('chat.calls.emptyBody', { family: familyName })
            }
            action={
              filter === 'all'
                ? {
                    label: t('chat.calls.newCall'),
                    icon: <Phone size={20} />,
                    onClick: newCall.onOpen,
                  }
                : undefined
            }
          />
        ) : (
          sections.map((section, index) => (
            <Box key={section.label}>
              <Text
                fontSize="13px"
                fontWeight={700}
                color="muted"
                pt={index === 0 ? '6px' : '14px'}
                pb={1}
              >
                {t(SECTION_LABELS[section.label])}
              </Text>
              {section.rows.map(({ call, count }) => {
                const info = detail(call, count);
                const video = call.callType === 'video';
                return (
                  <HStack key={call.id} spacing={3} py="10px">
                    <CallAvatar
                      call={call}
                      familyName={familyName}
                      familyId={currentFamilyId}
                    />
                    <Box flex="1" minW={0}>
                      <Text
                        fontWeight={700}
                        fontSize="17px"
                        color={info.missed ? 'accent' : undefined}
                        noOfLines={1}
                      >
                        {call.conversation.name}
                        {count > 1 && (
                          <Text as="span" fontWeight={600} fontSize="15px">
                            {' '}
                            ({count})
                          </Text>
                        )}
                      </Text>
                      <HStack
                        spacing={1}
                        fontSize="14px"
                        color={info.missed ? 'accent' : 'muted'}
                        fontWeight={info.missed ? 600 : 400}
                      >
                        {info.icon}
                        <Text as="span" noOfLines={1}>
                          {info.text}
                        </Text>
                      </HStack>
                    </Box>
                    <Text fontSize="13px" color="muted" flexShrink={0}>
                      {timeLabel(call.createdAt)}
                    </Text>
                    <IconButton
                      aria-label={t(
                        video
                          ? info.missed
                            ? 'chat.calls.videoCallBack'
                            : 'chat.calls.videoCallName'
                          : info.missed
                            ? 'chat.calls.callBackName'
                            : 'chat.calls.callName',
                        { name: call.conversation.name }
                      )}
                      icon={video ? <Video size={20} /> : <Phone size={19} />}
                      colorScheme="gray"
                      bg={info.missed ? 'accent.solid' : 'accent.tint'}
                      color={info.missed ? 'accent.onSolid' : 'accent'}
                      _hover={{ opacity: 0.9 }}
                      flexShrink={0}
                      isDisabled={state.status !== 'idle'}
                      onClick={() =>
                        void startCall(call.conversationId, call.callType)
                      }
                    />
                  </HStack>
                );
              })}
            </Box>
          ))
        )}

        {hasMore && (
          <Button
            mt={3}
            w="full"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            isLoading={loadingMore}
            onClick={() => void load(currentFamilyId, cursor)}
          >
            {t('chat.calls.older')}
          </Button>
        )}
      </Box>

      <NewCallDialog isOpen={newCall.isOpen} onClose={newCall.onClose} />
    </Flex>
  );
}
